import React, {useEffect, useState} from "react";
import {Box, Button, CircularProgress, Link, Typography} from "@mui/material";
import {downloadSharedFile, readShare, Share, ShareDetail as Detail, SharedRecord} from "../util/api";
import {when} from "./Shares";

/**
 * One share opened. A folder is browsed from the shared folder down, never above it; a project shows its header and
 * its released records; a document is its released revision. Every record is its LATEST RELEASED revision -- the
 * service never sends a draft -- and its files are that revision's.
 */
const HEADING = "#2B2B2B";
const MUTED = "#717171";
const BORDER = "#E0E0E0";
const BLUE3 = "#1498D8";

function size(bytes: number | null): string {
    if (bytes == null) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function RecordCard({share, record, onError}: {share: Share; record: SharedRecord; onError: (message: string) => void}) {
    const take = async (attachment: string) => {
        const result = await downloadSharedFile(share.USID, record.ObjectUSID, attachment);
        if (result.status === 200 && result.data?.Url) {
            window.location.href = result.data.Url;
            return;
        }
        onError(result.data?.Cause || "That download could not be prepared.");
    };
    return (
        <Box sx={{border: `1px solid ${BORDER}`, borderRadius: 2, p: 2.5, backgroundColor: "#FFFFFF"}}>
            <Typography sx={{fontSize: 15, fontWeight: 600, color: HEADING}}>
                {record.Number ? `${record.Number} · ${record.Name}` : record.Name}
            </Typography>
            <Typography sx={{fontSize: 12, color: MUTED, mt: 0.5}}>
                {record.Kind}{record.Revision ? ` · revision ${record.Revision}` : ""}{record.ReleasedDatetime ? ` · released ${when(record.ReleasedDatetime)}` : ""}
            </Typography>
            {record.Description && <Typography sx={{fontSize: 13, color: HEADING, mt: 1, lineHeight: 1.6}}>{record.Description}</Typography>}
            {record.Files.length > 0 ? (
                <Box sx={{mt: 1.5, display: "flex", flexDirection: "column", gap: 0.5}}>
                    {record.Files.map(file => file.Missing
                        ? <Typography key={file.AttachmentUSID} sx={{fontSize: 13, color: MUTED}}>
                              {file.Name} &mdash; no longer in storage; ask your contact to attach it again
                          </Typography>
                        : <Link key={file.AttachmentUSID} component="button" type="button" onClick={() => take(file.AttachmentUSID)}
                                sx={{fontSize: 13, color: BLUE3, textAlign: "left", alignSelf: "flex-start"}}>
                              {file.Name}{file.Bytes != null ? ` (${size(file.Bytes)})` : ""}
                          </Link>)}
                </Box>
            ) : <Typography sx={{fontSize: 12, color: MUTED, mt: 1}}>No files with this record.</Typography>}
        </Box>
    );
}

export default function ShareDetail({summary, onBack}: {summary: Share; onBack: () => void}) {
    const [folder, setFolder] = useState<string | undefined>(undefined);
    const [detail, setDetail] = useState<Detail | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [downloadError, setDownloadError] = useState<string | null>(null);

    useEffect(() => {
        setDetail(null);
        setError(null);
        readShare(summary.USID, folder).then(result => {
            if (result.status === 200) setDetail(result.data);
            else setError(result.data?.Cause || result.data?.Code || "That could not be opened.");
        });
    }, [summary.USID, folder]);

    const records = detail?.Document ? [detail.Document] : (detail?.Records ?? []);

    const title = detail?.Project
        ? (detail.Project.Number ? `${detail.Project.Number} · ${detail.Project.Name}` : detail.Project.Name)
        : detail?.Folder?.Name ?? (summary.Number ? `${summary.Number} · ${summary.Name}` : summary.Name);

    return (
        <Box sx={{maxWidth: 900, mx: "auto", px: 3, py: 4, boxSizing: "border-box"}}>
            <Button onClick={onBack} sx={{textTransform: "none", px: 0, color: BLUE3}}>&larr; Back</Button>

            {detail?.Path && detail.Path.length > 1 && (
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1, fontSize: 13, color: MUTED}}>
                    {detail.Path.map((one, index) => (
                        <React.Fragment key={one.FolderUSID}>
                            {index > 0 && <span>/</span>}
                            {index < detail.Path!.length - 1
                                ? <Link component="button" type="button" onClick={() => setFolder(one.FolderUSID)}
                                        sx={{fontSize: 13, color: BLUE3}}>{one.Name}</Link>
                                : <span>{one.Name}</span>}
                        </React.Fragment>
                    ))}
                </Box>
            )}

            <Typography component="h1" sx={{fontSize: 24, fontWeight: 500, color: HEADING, mt: 1}}>{title}</Typography>
            <Typography sx={{fontSize: 13, color: MUTED, mt: 0.5}}>
                {summary.From ? `From ${summary.From}` : "From your supplier"}
                {detail?.Project?.Status ? ` · ${detail.Project.Status.toLowerCase()}` : ""}
            </Typography>
            {summary.Note && <Typography sx={{fontSize: 14, color: HEADING, mt: 1.5}}>{summary.Note}</Typography>}
            {(detail?.Project?.Description || detail?.Folder?.Description) && (
                <Typography sx={{fontSize: 14, color: MUTED, mt: 1.5, lineHeight: 1.6}}>
                    {detail?.Project?.Description || detail?.Folder?.Description}
                </Typography>
            )}

            {error && <Typography sx={{fontSize: 14, color: "#B3261E", mt: 4}}>{error}</Typography>}
            {downloadError && <Typography sx={{fontSize: 14, color: "#B3261E", mt: 2}}>{downloadError}</Typography>}
            {!detail && !error && <Box sx={{display: "flex", justifyContent: "center", py: 8}}><CircularProgress size={24}/></Box>}

            {detail && (
                <>
                    {(detail.Subfolders ?? []).length > 0 && (
                        <Box sx={{mt: 3, display: "flex", flexDirection: "column", gap: 1}}>
                            {detail.Subfolders!.map(one => (
                                <Box key={one.FolderUSID} onClick={() => setFolder(one.FolderUSID)}
                                     sx={{border: `1px solid ${BORDER}`, borderRadius: 2, px: 2.5, py: 1.5, cursor: "pointer",
                                          fontSize: 14, color: HEADING, backgroundColor: "#FAFBFC", "&:hover": {borderColor: BLUE3}}}>
                                    {one.Name}
                                </Box>
                            ))}
                        </Box>
                    )}
                    <Box sx={{mt: 3, display: "flex", flexDirection: "column", gap: 1.5}}>
                        {records.map(one => <RecordCard key={one.ObjectUSID} share={summary} record={one} onError={setDownloadError}/>)}
                    </Box>
                    {/* SAID, NOT LEFT TO BE INFERRED: an empty folder and a folder of unreleased drafts look the same. */}
                    <Typography sx={{fontSize: 12, color: MUTED, mt: 3}}>
                        {records.length === 0 && (detail.Subfolders ?? []).length === 0 ? "Nothing released here yet. " : ""}
                        Only released records are shown, at their latest released revision.
                    </Typography>
                </>
            )}
        </Box>
    );
}
